import React, { useState } from 'react';
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  limit
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Swal from 'sweetalert2';

const CorreccionPropiedades: React.FC = () => {
  const [ejecutando, setEjecutando] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    corregidas: 0,
    sinCambios: 0
  });

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
    console.log(message);
  };

  const analizarPropiedades = async () => {
    addLog('🔍 Analizando propiedades...');

    try {
      const propiedadesRef = collection(db, 'propiedades');
      const q = query(propiedadesRef, limit(20));
      const snapshot = await getDocs(q);

      addLog(`📊 Muestra de ${snapshot.size} propiedades`);
      addLog('');

      let problemasEncontrados = {
        sinTitulo: 0,
        sinDireccion: 0,
        sinTipo: 0,
        valoresEnCero: 0
      };

      snapshot.docs.forEach((docSnap, index) => {
        const data = docSnap.data();

        if (index < 5) { // Mostrar solo las primeras 5
          addLog(`--- Propiedad ${index + 1} ---`);
          addLog(`  List Number: ${data.list_number || 'N/A'}`);
          addLog(`  Título: ${data.titulo || 'SIN TÍTULO'}`);
          addLog(`  Tipo: ${data.tipo_propiedad || 'N/A'}`);
          addLog(`  Área: ${data.direccion?.area || 'N/A'}`);
          addLog(`  Recámaras: ${data.recamaras || 0}`);
          addLog(`  Baños: ${data.banos_completos || 0}`);
          addLog('');
        }

        if (!data.titulo || data.titulo === 'Sin título') problemasEncontrados.sinTitulo++;
        if (!data.direccion?.area) problemasEncontrados.sinDireccion++;
        if (!data.tipo_propiedad) problemasEncontrados.sinTipo++;
        if ((data.recamaras || 0) === 0 && (data.banos_completos || 0) === 0) {
          problemasEncontrados.valoresEnCero++;
        }
      });

      addLog('📈 RESUMEN DE PROBLEMAS:');
      addLog(`  ❌ Sin título: ${problemasEncontrados.sinTitulo}`);
      addLog(`  ❌ Sin dirección: ${problemasEncontrados.sinDireccion}`);
      addLog(`  ❌ Sin tipo: ${problemasEncontrados.sinTipo}`);
      addLog(`  ⚠️  Valores en cero: ${problemasEncontrados.valoresEnCero}`);

    } catch (error: any) {
      addLog(`❌ Error al analizar: ${error.message}`);
    }
  };

  const corregirPropiedades = async () => {
    const resultado = await Swal.fire({
      title: '⚠️ Confirmar Corrección',
      html: `
        <div style="text-align: left;">
          <p>Se corregirán las propiedades con:</p>
          <ul>
            <li>✅ Títulos faltantes</li>
            <li>✅ Direcciones incompletas</li>
            <li>✅ Tipos de propiedad faltantes</li>
          </ul>
          <p><strong>¿Continuar?</strong></p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, corregir',
      cancelButtonText: 'Cancelar'
    });

    if (!resultado.isConfirmed) {
      addLog('❌ Corrección cancelada');
      return;
    }

    setEjecutando(true);
    addLog('🔧 Iniciando corrección...');

    try {
      const propiedadesRef = collection(db, 'propiedades');
      const snapshot = await getDocs(propiedadesRef);

      setStats(prev => ({ ...prev, total: snapshot.size }));
      addLog(`📊 Total propiedades: ${snapshot.size}`);

      let batch = writeBatch(db);
      let operacionesEnBatch = 0;
      let corregidas = 0;
      let sinCambios = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const updates: any = {};
        let necesitaActualizacion = false;

        // 1. Corregir título
        if (!data.titulo || data.titulo === 'Sin título') {
          updates.titulo = data.list_number
            ? `Propiedad ${data.list_number}`
            : `${data.tipo_propiedad || 'Propiedad'} en ${data.direccion?.area || 'Riviera Nayarit'}`;
          necesitaActualizacion = true;
        }

        // 2. Corregir dirección
        if (!data.direccion?.area) {
          updates.direccion = {
            calle: data.direccion?.calle || '',
            numero: data.direccion?.numero || '',
            unidad: data.direccion?.unidad || '',
            area: 'Riviera Nayarit',
            zona: data.direccion?.zona || 'Riviera Nayarit',
            ciudad: data.direccion?.ciudad || 'Puerto Vallarta',
            estado: data.direccion?.estado || 'Nayarit',
            codigo_postal: data.direccion?.codigo_postal || ''
          };
          necesitaActualizacion = true;
        }

        // 3. Corregir tipo de propiedad
        if (!data.tipo_propiedad) {
          updates.tipo_propiedad = 'Residencial';
          necesitaActualizacion = true;
        }

        if (necesitaActualizacion) {
          const propRef = doc(db, 'propiedades', docSnap.id);
          batch.update(propRef, updates);
          operacionesEnBatch++;
          corregidas++;

          if (operacionesEnBatch >= 500) {
            await batch.commit();
            addLog(`  ✅ Batch: ${corregidas} corregidas`);
            batch = writeBatch(db);
            operacionesEnBatch = 0;
          }
        } else {
          sinCambios++;
        }

        setStats({ total: snapshot.size, corregidas, sinCambios });
      }

      // Commit final
      if (operacionesEnBatch > 0) {
        await batch.commit();
      }

      addLog('');
      addLog('✅ CORRECCIÓN COMPLETADA');
      addLog(`  Total: ${snapshot.size}`);
      addLog(`  Corregidas: ${corregidas}`);
      addLog(`  Sin cambios: ${sinCambios}`);

      await Swal.fire({
        icon: 'success',
        title: '✅ Corrección Completada',
        html: `
          <div style="text-align: left;">
            <p><strong>Total propiedades:</strong> ${snapshot.size}</p>
            <p><strong>Corregidas:</strong> ${corregidas}</p>
            <p><strong>Sin cambios:</strong> ${sinCambios}</p>
          </div>
        `
      });

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message
      });
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🔧 Corrección de Propiedades Migradas</h1>

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={analizarPropiedades}
          disabled={ejecutando}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: ejecutando ? '#ccc' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: ejecutando ? 'not-allowed' : 'pointer',
            marginRight: '10px'
          }}
        >
          🔍 Analizar Muestra
        </button>

        <button
          onClick={corregirPropiedades}
          disabled={ejecutando}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: ejecutando ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: ejecutando ? 'not-allowed' : 'pointer'
          }}
        >
          {ejecutando ? '⏳ Corrigiendo...' : '✅ Corregir Todas'}
        </button>
      </div>

      {/* Estadísticas */}
      {stats.total > 0 && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h3>📊 Estadísticas</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <div>
              <strong>Total:</strong> {stats.total}
            </div>
            <div style={{ color: '#28a745' }}>
              <strong>Corregidas:</strong> {stats.corregidas}
            </div>
            <div style={{ color: '#6c757d' }}>
              <strong>Sin cambios:</strong> {stats.sinCambios}
            </div>
          </div>
        </div>
      )}

      {/* Log */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px',
        maxHeight: '500px',
        overflow: 'auto'
      }}>
        <h3>📋 Log</h3>
        <div style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          whiteSpace: 'pre-wrap'
        }}>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CorreccionPropiedades;
